import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useServiceRequestAPI } from 'src/pages/ServiceRequest/CustomHooks/useServiceRequestAPI';
import { useNotesDocsAPI } from 'src/SharedCustomHooks/UseNotesAPI/useNotesDocsAPI';

interface ServiceRequestDataType {
  serviceRequest: any;
  activity: any[];
  documents: any[];
  messages: any[];
  loading: boolean;
  error: string | null;
  oneYearWarrantyIssues: any;
  loadingMessages: boolean;
  loadingNotesDocs: boolean;
}

interface ServiceRequestActionsType {
  fetchServiceRequestById: (id: string) => Promise<void>;
  refreshData: (serviceRequestId: string) => Promise<void>;
  updateStatus: (statusData: any, serviceRequestId: string) => Promise<void>;
  saveScheduledAppointmentFromAPI: (
    formData: any,
    serviceRequestId: string
  ) => Promise<void>;
  deleteServiceRequestFromAPI: (serviceRequestId: string) => Promise<boolean>;
  updateServiceRequestOneYearWarrantyIssueStatusFromAPI: (
    data: any
  ) => Promise<void>;
  postMessageToAPI: (messageData: any) => Promise<void>;
  saveDocument: (
    document: any,
    serviceRequestId: string,
    userId: string,
    dynamicAdditionalData?: Record<string, any>,
    options?: any
  ) => Promise<void>;
  deleteDocument: (document: any, serviceRequestId: string) => Promise<void>;
  fetchDocuments: (serviceRequestId: string) => Promise<void>;
}

const ServiceRequestDataContext = createContext<
  ServiceRequestDataType | undefined
>(undefined);
const ServiceRequestActionsContext = createContext<
  ServiceRequestActionsType | undefined
>(undefined);

interface ServiceRequestProviderProps {
  children: ReactNode;
}

export const ServiceRequestProvider = ({
  children,
}: ServiceRequestProviderProps) => {
  const serviceRequestAPI = useServiceRequestAPI();
  const notesAPI = useNotesDocsAPI(); // Only for document operations

  const refreshData = async (serviceRequestId: string): Promise<void> => {
    serviceRequestAPI.clearServiceRequestState();

    await serviceRequestAPI.fetchServiceRequestById(serviceRequestId);

    await Promise.all([
      serviceRequestAPI.fetchServiceRequestActivity(serviceRequestId),
      serviceRequestAPI.fetchMessagesFromAPI(serviceRequestId),
      notesAPI.fetchNotesAndDocuments(serviceRequestId),
    ]);
  };

  const fetchDocuments = async (serviceRequestId: string): Promise<void> => {
    await notesAPI.fetchNotesAndDocuments(serviceRequestId);
  };

  const deleteServiceRequestFromAPI = async (
    serviceRequestId: string
  ): Promise<boolean> => {
    const result =
      await serviceRequestAPI.deleteServiceRequestFromAPI(serviceRequestId);

    if (result) {
      notesAPI.clearNotesState();
    }

    return result;
  };

  const saveDocument = async (
    document: any,
    serviceRequestId: string,
    userId: string,
    dynamicAdditionalData?: Record<string, any>,
    options?: any
  ): Promise<void> => {
    await notesAPI.saveDocument(
      document,
      serviceRequestId,
      userId,
      dynamicAdditionalData,
      options
    );
  };

  const deleteDocument = async (
    document: any,
    serviceRequestId: string
  ): Promise<void> => {
    try {
      const documentWithType = {
        ...document,
        Type: 'DOCUMENTS',
      };

      await notesAPI.deleteDocument(documentWithType, serviceRequestId);
    } catch (error) {
      console.error('Error deleting service request document:', error);
      throw error;
    }
  };

  const dataValue = useMemo(
    () => ({
      // Service request data
      serviceRequest: serviceRequestAPI.serviceRequest,
      activity: serviceRequestAPI.activity,
      messages: serviceRequestAPI.messages,
      loading: serviceRequestAPI.loading,
      error: serviceRequestAPI.error,
      oneYearWarrantyIssues: serviceRequestAPI.oneYearWarrantyIssues,
      loadingMessages: serviceRequestAPI.loadingMessages,
      // Documents data only (not notes)
      documents: notesAPI.documents,
      loadingNotesDocs: notesAPI.loadingNotesDocs,
    }),
    [
      serviceRequestAPI.serviceRequest,
      serviceRequestAPI.activity,
      serviceRequestAPI.messages,
      serviceRequestAPI.loading,
      serviceRequestAPI.error,
      serviceRequestAPI.oneYearWarrantyIssues,
      serviceRequestAPI.loadingMessages,
      notesAPI.documents, // Only documents
      notesAPI.loadingNotesDocs,
    ]
  );

  const actionsValue = useMemo(
    () => ({
      // Service request actions.
      fetchServiceRequestById: serviceRequestAPI.fetchServiceRequestById,
      updateStatus: serviceRequestAPI.updateStatus,
      saveScheduledAppointmentFromAPI:
        serviceRequestAPI.saveScheduledAppointmentFromAPI,
      updateServiceRequestOneYearWarrantyIssueStatusFromAPI:
        serviceRequestAPI.updateServiceRequestOneYearWarrantyIssueStatusFromAPI,
      postMessageToAPI: serviceRequestAPI.postMessageToAPI,
      // Document-only actions on the Notes API.
      saveDocument,
      deleteDocument,
      fetchDocuments,
      // Coordinated actions.
      refreshData,
      deleteServiceRequestFromAPI,
    }),
    [
      serviceRequestAPI.fetchServiceRequestById,
      serviceRequestAPI.updateStatus,
      serviceRequestAPI.saveScheduledAppointmentFromAPI,
      serviceRequestAPI.updateServiceRequestOneYearWarrantyIssueStatusFromAPI,
      serviceRequestAPI.postMessageToAPI,
      saveDocument,
      deleteDocument,
      fetchDocuments,
      refreshData,
      deleteServiceRequestFromAPI,
    ]
  );

  return (
    <ServiceRequestActionsContext.Provider value={actionsValue}>
      <ServiceRequestDataContext.Provider value={dataValue}>
        {children}
      </ServiceRequestDataContext.Provider>
    </ServiceRequestActionsContext.Provider>
  );
};

export const useServiceRequestDataFromContext = (): ServiceRequestDataType => {
  const context = useContext(ServiceRequestDataContext);

  if (context === undefined) {
    throw new Error(
      'useServiceRequestDataFromContext must be used within a ServiceRequestProvider'
    );
  }

  return context;
};

export const useServiceRequestActionsFromContext =
  (): ServiceRequestActionsType => {
    const context = useContext(ServiceRequestActionsContext);

    if (context === undefined) {
      throw new Error(
        'useServiceRequestActionsFromContext must be used within a ServiceRequestProvider'
      );
    }

    return context;
  };
